import { Player, type PlayerOptions } from "./player";
import type {
  Stream,
  Session,
  Mix,
  PlayMixResult,
  TranscriptionEvent,
} from "../types/model.types";
import { debugConfig } from "../utils/debug-config";

// Declare the native interface that will be injected by the host platform's WebView
declare const NativeBridge: {
  onStateChange: (state: string, error: string | null) => void;
  onTranscriptionEvent: (event: string, fullTranscript: string | null) => void;
  onStreamMetrics: (metrics: string) => void;
  onFrequencyData: (frequencyData: string) => void;
  runAudioDeviceMonitor: (start: boolean) => void;
  runScreenCaptureMonitor: (start: boolean) => void;
  bindNetworkInterface: (interface: string | null) => void;
};

/**
 * NativePlayer is a wrapper around Player that handles JSON encoding/decoding
 * for native bridge communication.
 */
export class NativePlayer {
  private player: Player;

  constructor(options: PlayerOptions) {
    console.log("[NativePlayer] Constructor");
    this.player = new Player(options);
  }

  /**
   * Select stream with JSON encoded parameters
   */
  public selectStream(
    streamJson: string,
    sessionJson: string,
    mixJson: string,
  ): void {
    try {
      const stream: Stream = JSON.parse(streamJson);
      const session: Session = JSON.parse(sessionJson);
      const mix: Mix = JSON.parse(mixJson);
      this.player.selectStream(stream, session, mix);
    } catch (error) {
      console.error("Failed to parse selectStream parameters:", error);
      throw new Error("Invalid JSON parameters for selectStream");
    }
  }

  /**
   * Play stream with JSON encoded parameter
   */
  public playStream(streamJson: string): void {
    try {
      const stream: Stream = JSON.parse(streamJson);
      this.player.playStream(stream);
    } catch (error) {
      console.error("Failed to parse playStream parameter:", error);
      throw new Error("Invalid JSON parameter for playStream");
    }
  }

  /**
   * Play mix with JSON encoded parameter and return JSON
   * Returns only session and stream (excludes mix and selectStream for native compatibility)
   */
  public play(mixJson: string): { session: Session; stream: Stream | null } | null {
    console.log("[NativePlayer] Playing mix:", mixJson);
    try {
      const mix: Mix = JSON.parse(mixJson);
      const result: PlayMixResult | null = this.player.play(mix);
      if (!result) return null;
      // Return only fields expected by native layer
      return {
        session: result.session,
        stream: result.stream,
      };
    } catch (error) {
      console.error("Failed to parse play parameter:", error);
      throw new Error("Invalid JSON parameter for play");
    }
  }

  /**
   * Get full transcript as JSON string
   */
  public getFullTranscript(): string {
    const transcript: TranscriptionEvent[] = this.player.getFullTranscript();
    return JSON.stringify(transcript);
  }

  /**
   * Get current stream as JSON string
   */
  public get stream(): string | null {
    const stream = this.player.stream;
    return stream ? JSON.stringify(stream) : null;
  }

  /**
   * Get current mix as JSON string
   */
  public get mix(): string | null {
    const mix = this.player.mix;
    return mix ? JSON.stringify(mix) : null;
  }

  /**
   * Get current session as JSON string
   */
  public get session(): string | null {
    const session = this.player.session;
    return session ? JSON.stringify(session) : null;
  }

  /**
   * Get current state as string
   */
  public get state(): string {
    return this.player.state;
  }

  /**
   * Get current caption
   */
  public get currentCaption(): string | null {
    return this.player.currentCaption;
  }

  /**
   * Check if player can play
   */
  public get canPlay(): boolean {
    return this.player.canPlay;
  }

  /**
   * Stop playback
   */
  public stop(): void {
    console.log("[NativePlayer] stop");
    this.player.stop();
  }

  /**
   * Check if currently playing
   */
  public isPlaying(): boolean {
    return this.player.isPlaying();
  }

  /**
   * Handle native error with JSON string
   */
  public handleNativeError(errorJson: string): void {
    console.log("[NativePlayer] handleNativeError", errorJson);
    this.player.handleNativeError(errorJson);
  }

  /**
   * Handle audio device change
   */
  public onAudioDeviceChanged(deviceType: string): void {
    console.log("[NativePlayer] onAudioDeviceChanged", deviceType);
    this.player.onAudioDeviceChanged(deviceType);
  }

  /**
   * Handle screen capture status change
   */
  public onScreenCaptureStatusChanged(isScreenCaptureActive: boolean): void {
    console.log(
      "[NativePlayer] onScreenCaptureStatusChanged",
      isScreenCaptureActive,
    );
    this.player.onScreenCaptureStatusChanged(isScreenCaptureActive);
  }

  /**
   * Network binding succeeded
   */
  public networkBindingSucceeded(): void {
    console.log("[NativePlayer] networkBindingSucceeded");
    this.player.networkBindingSucceeded();
  }

  /**
   * Clear transcript
   */
  public clearTranscript(): void {
    this.player.clearTranscript();
  }

  /**
   * Get isFullTranscriptEnabled
   */
  public get isFullTranscriptEnabled(): boolean {
    return this.player.isFullTranscriptEnabled;
  }

  /**
   * Set isFullTranscriptEnabled
   */
  public setIsFullTranscriptEnabled(enabled: boolean): void {
    console.log("[NativePlayer] setIsFullTranscriptEnabled", enabled);
    this.player.setIsFullTranscriptEnabled(enabled);
  }

  /**
   * Get isScreenCaptureAllowed
   */
  public get isScreenCaptureAllowed(): boolean {
    return this.player.isScreenCaptureAllowed;
  }

  /**
   * Set isScreenCaptureAllowed
   */
  public setIsScreenCaptureAllowed(allowed: boolean): void {
    console.log("[NativePlayer] setIsScreenCaptureAllowed", allowed);
    this.player.setIsScreenCaptureAllowed(allowed);
  }

  /**
   * Get isFrequencyAnalysisEnabled
   */
  public get isFrequencyAnalysisEnabled(): boolean {
    return this.player.isFrequencyAnalysisEnabled;
  }

  /**
   * Set isFrequencyAnalysisEnabled
   */
  public setIsFrequencyAnalysisEnabled(enabled: boolean): void {
    console.log("[NativePlayer] setIsFrequencyAnalysisEnabled", enabled);
    this.player.setIsFrequencyAnalysisEnabled(enabled);
  }

  /**
   * Destroy player
   */
  public destroy(): void {
    this.player.destroy();
  }
}

// Static instance for singleton pattern
let _nativePlayerInstance: NativePlayer | null = null;

/**
 * Creates a new native player singleton instance.
 * If a player already exists, it will be replaced with a new instance.
 * @returns The new native player instance
 */
export function createPlayer(enableStreamMonitoring: boolean = false): NativePlayer {
  if (enableStreamMonitoring) {
    debugConfig.setEnableStreamMonitoring(true);
  }
  
  console.debug("createPlayer");
  const player = new NativePlayer({
    onStateChange: (state, error) => {
      console.log("[NativePlayer] onStateChange:", state, error);
      NativeBridge.onStateChange(state, error ? JSON.stringify(error) : null);
    },
    onTranscriptionEvent: (event, fullTranscript) => {
      NativeBridge.onTranscriptionEvent(
        JSON.stringify(event),
        fullTranscript ? JSON.stringify(fullTranscript) : null,
      );
    },
    onStreamMetrics: (metrics) => {
      NativeBridge.onStreamMetrics(JSON.stringify(metrics));
    },
    onFrequencyData: (frequencyData) => {
      NativeBridge.onFrequencyData(JSON.stringify(frequencyData));
    },
    runAudioDeviceMonitor: (start) => {
      console.log("[NativePlayer] runAudioDeviceMonitor:", start);
      NativeBridge.runAudioDeviceMonitor(start);
    },
    runScreenCaptureMonitor: (start) => {
      console.log("[NativePlayer] runScreenCaptureMonitor:", start);
      NativeBridge.runScreenCaptureMonitor(start);
    },
    bindNetworkInterface: (networkInterface) => {
      console.log("[NativePlayer] bindNetworkInterface:", networkInterface);
      NativeBridge.bindNetworkInterface(
        networkInterface ? JSON.stringify(networkInterface) : null,
      );
    },
  });
  _nativePlayerInstance = player;
  return player;
}

/**
 * Gets the current player instance.
 * @returns The current player instance or null if none exists
 */
export function getPlayer(): NativePlayer | null {
  return _nativePlayerInstance;
}
